import { createContext, useState, useContext, useRef, useLayoutEffect, useEffect } from 'react'
import useMapDataContext, { MapData, MapView } from '@contexts/mapDataContext'
import useWindowVisibilityContext from './windowVisibilityContext'
import useCurrentLocationContext from './currentLocationContext'
import { useAuthContext } from './authContext'
import axios from '@utils/webRequests'
import ActionCable from 'actioncable'
import guid from '@utils/guid'
import useStateRef from 'react-usestateref'

const CableEmitter = (function() {
  const handlers = {}

  // Used to register new event types
  const registerEventType = (eventName) => {
    if (Array.isArray(handlers[eventName])) {
      console.error(`Event: "${eventName}" is already added to CableEmitter`)
    } else {
      handlers[eventName] = []
    }
  }

  // Adds listeners in child components to get relevant data
  const addEventListener = (eventName, callback) => {
    if (Array.isArray(handlers[eventName])) {
      const callbackId = guid()
      handlers[eventName].push({ callback, callbackId, })
      return callbackId
    } else {
      console.log(`Event: "${eventName}" is not a valid event for CableEmitter`)
    }
  }
  
  // Removes unused listeners for cleanup
  const removeEventListener = (eventName, callbackId) => {
    if (Array.isArray(handlers[eventName])) {
      handlers[eventName] = handlers[eventName]?.filter((handler) => { 
        return handler?.callbackId !== callbackId
      })
      return true
    } else {
      console.error(`Event: "${eventName}" is not a valid event for CableEmitter`)
      return false
    }
  }

  // Emits the event from our actioncable callback
  const emitEvent = (eventName, data) => {
    if (Array.isArray(handlers[eventName])) {
      handlers[eventName].forEach(({ callback }, index) => callback(data, index))
    } else {
      console.error(`Event: "${eventName}" is not a valid event for CableEmitter`)
    }
  }

  // EVENT TYPE REGISTRATION
  registerEventType('UserMessage')
  registerEventType('UserEvents')

  return {
    registerEventType,
    addEventListener,
    removeEventListener,
    emitEvent,
    handlers,
  }
}())

const CableContext = createContext();
const CableProvider = ({ children }) => {
  const { isWebsocketConnected, setIsWebsocketConnected, nearbyProfileRefreshRequest } = useMapDataContext()
  const { state: authState, getUser, handleInstantUpdate } = useAuthContext()
  const { currentLocation, currentLocationRef } = useCurrentLocationContext()
  const messageChannelRef = useRef(null)
  const userEventsChannelRef = useRef(null)
  const [userEventsChannelClient, setUserEventsChannelClient] = useState(null)
  const [userEventsIsConnected, setUserEventsIsConnected] = useState(false)
  const [consumer, setConsumer, consumerRef] = useStateRef(null)
  const activityPulseOnCooldown = useRef(false)
  const [didSendConnectMessage, setDidSendConnectMessage] = useState(false)
  const messagesDisconnectedAt = useRef(null)
  const userEventsDisconnectedAt = useRef(null)
  const conversationsRefreshRequestRef = useRef(null)
  const { isWindowVisible } = useWindowVisibilityContext()
  // reAuthToggle is used to toggle the websocket connect logic so we can ensure it fires
  const [reAuthToggle, setReAuthToggle, reAuthToggleRef] = useStateRef(false)
  // determines if we are disconnected and should reauthenticate websocket request
  const [shouldReauthWebsocket, setShouldReauthWebsocket, shouldReauthWebsocketRef] = useStateRef(false)
  // loading state for reconnecting
  const [isReconnecting, setIsReconnecting] = useState(false)
  // sets a disconnected timeout that only attempts to reconnect after user was away for 60s (slow reconnection logic)
  const disconnectedTimeout = useRef(null)
  // sets a timeout that aggressively attempts reconnection every 5 seconds
  const reconnectTimeout = useRef(null)

  // creates initial consumer (setting to state directly does not work)
  useLayoutEffect(() => {
    if (!consumer) {
      const newConsumer = ActionCable.createConsumer()
      setConsumer(newConsumer)
    }
  }, [consumer])
  
  // Gets our websocket authorization JWT on initial render and then makes the connection
  // Our callbacks call the appropriate event emitters to pass data into our event handlers
  useLayoutEffect(() => {
    if (!consumer) return;
    if (isReconnecting) return;
    // We need to check the visiblity state directly because isWindowVisible is very unreliable.
    if (!document.visibilityState === 'visible') return;
    if ((authState.user && authState.authenticated && !isWebsocketConnected) || shouldReauthWebsocketRef.current) {
      setIsReconnecting(true)
      console.log('REQUESTING WEBSOCKET TOKEN')
      axios.get('/api/auth/websocket_connect')
        .then((res) => {
          const jwt = res.data
          const protocol = process.env.NODE_ENV === 'production' ? 'wss' : 'ws'
          const actionCableUrl = `${protocol}://${process.env.REACT_APP_API_DOMAIN}/cable?websocket_connect_jwt=${jwt}`
          consumer.url = actionCableUrl
          consumer.connection.open()
          initializeWebsocketHandlers()
          console.log(consumer)
        })
        .catch((err) => {
          console.log(err)
          setTimeout(() => {
            // set reconnecting false in timeout so we don't run the function
            // in an infinite loop if it fails immediately
            setIsReconnecting(false)
          }, 5_000)
        })
    } else if (!authState.authenticated && isWebsocketConnected) {
      console.log('ACTIONCABLE CONSUMER DISCONNECTED FOR USER LOGOUT')
      consumer?.disconnect()
      setUserEventsChannelClient(null)
      messageChannelRef.current = null
      userEventsChannelRef.current = null
      setIsWebsocketConnected(false)
    }
  }, [authState?.user?.nearbyUuid, authState?.authenticated, currentLocation, isWebsocketConnected, isWindowVisible, reAuthToggle, isReconnecting])

  // Sets listener for the page entering the Back/Forward Cache which is what the browser uses to freeze the page when the users leaves it
  useEffect(() => {
    window.addEventListener('pageShow', handlePageShowEvent)
    return () => window.removeEventListener('pageShow', handlePageShowEvent)
  }, [])

  const handlePageShowEvent = (event) => {
    // event.persisted is true if the page was restored from the bfcache
    if (event.persisted || shouldReauthWebsocketRef.current) {
      console.log('ATTEMPTING RECONNECTING BECAUSE PAGE ENTERED BACK/FORWARD CACHE');
      setReconnecting()
    }
  }

  // Sets listener for the page becoming visible again
  useLayoutEffect(() => {
    document.addEventListener('visibilitychange', visibilityListener)
    return () => document.removeEventListener('visibilitychange', visibilityListener)
  }, [])

  const visibilityListener = () => {
    if (document.visibilityState !== 'hidden' && shouldReauthWebsocketRef.current) {
      console.log('ATTEMPTING RECONNECTION BECAUSE USER RETURNED TO TAB DISCONNECTED');
      setReconnecting()
    }
  }

  // Fires the "connected" user event when we connect to backend
  // NOTE: We're putting it in this useEffect so that we can wait for the user's
  //       current location without delaying the entire websocket connection cascade.
  useLayoutEffect(() => {
    if (didSendConnectMessage) return;
    if (userEventsChannelClient && currentLocation && userEventsIsConnected) {
      connectSelf()
      setDidSendConnectMessage(true)
    }
  }, [currentLocation, userEventsChannelClient, userEventsIsConnected, didSendConnectMessage])

  const initializeWebsocketHandlers = () => {
    // webSocket events: onopen onclose onerror onmessage
    consumer.connection.webSocket.onopen = () => {
      console.log('WEBSOCKET CONNECTION OPEN')
      messageChannelSubscription(consumer)
      userEventsChannelSubscription(consumer)
      setShouldReauthWebsocket(false)
      setIsWebsocketConnected(true)
      setIsReconnecting(false)
    }
    
    // NOTE: the onclose doesn't typically fire when the server closes the connection,
    //       or you lose the connection. Hence the connection error handling being done
    //       in the channel subscription event handlers.
    consumer.connection.webSocket.onclose = () => {
      console.log('WEBSOCKET CONNECTION CLOSED')
      // WARNING: Make sure the disconnectedAt refs are actually being set (why we have this here and in the channel disconnected handler).
      //          Sometimes the socket will close and not call the channel disconnection
      //          And other times the channel will close and the socket won't be disconnected... :(
      setDisconnected()
    }

    // consumer.connection.webSocket.onerror = (res) => {
    //   console.log('WEBSOCKET CONNECTION ERROR')
    //   console.log(res)
    //   // TODO: consumer.connection.disconnect()
    // }

    // consumer.connection.webSocket.onmessage = (res) => {
    //   console.log('WEBSOCKET CONNECTION MESSAGE')
    //   console.log(res)
    //   // TODO: consumer.connection.disconnect()
    // }

    // consumer.connection.webSocket.addEventListener('error', (e) => {
    //   console.log('=========================')
    //   console.log('IN ERROR EVENT HANDLER')
    //   console.log(e)
    //   console.log('=========================')
    // })

  }

  const messageChannelSubscription = (consumer) => {
    if (messageChannelRef.current) return;
    messageChannelRef.current = consumer.subscriptions.create("MessageChannel", {
      connected: () => {
        console.log('MESSAGE CHANNEL CONNECTED')
        refreshMessagesIfStale()
      },
      disconnected: () => {
        console.log('MESSAGE CHANNEL DISCONNECTED')
        setDisconnected()
        console.log('messages disconnected at ', messagesDisconnectedAt.current)
      },
      received: (data) => {
        console.log('RECEIVED DATA: MESSAGE CHANNEL')
        CableEmitter.emitEvent('UserMessage', data)
      }
    })
  }

  const userEventsChannelSubscription = (consumer) => {
    userEventsChannelRef.current = consumer.subscriptions.create("UserEventsChannel", {
      connected: () => {
        console.log('USER EVENT CHANNEL CONNECTED')
        // NOTE: Were setting the didSendConnectMessage state here to false because there are instances where
        //       the server will disconnect from actioncable js, but not fire the disconnected callback
        //       (this happens a lot in dev), so we need to manually "appear" again to other users when reconnected.
        setShouldReauthWebsocket(false)
        setDidSendConnectMessage(false)
        setUserEventsIsConnected(true)
        refreshProfilesIfStale()
      },
      disconnected: () => {
        console.log('USER EVENT CHANNEL DISCONNECTED')
        setDisconnected()
        console.log('user events disconnected at ', userEventsDisconnectedAt.current)
      },
      received: (data) => {
        const nearbyUuid = (data?.user?.nearbyUuid || data?.nearby_uuid)
        if (!getUser() || nearbyUuid === authState?.user?.nearbyUuid) return;
        switch (data.action) {
          case 'update':
            MapView.updateUser(data.user.nearbyUuid, data.user)
            break
          case 'reconnected':
            MapView.updateUser(data.user.nearbyUuid, data.user)
            break
          case 'connected':
            if (!MapData.getProfile(data?.user?.nearbyUuid)) {
              MapView.addFromData(data?.user, { recalcDatum: true })
            } else {
              MapView.updateUser(data.user.nearbyUuid, data.user)
            }
            break
          case 'disappear':
            MapView.removeUser(data.nearby_uuid)
            break
          case 'user_activity':
            MapView.userActivity(data?.nearby_uuid)
            break
        }
        CableEmitter.emitEvent('UserEvents', data)
      }
    })
    setUserEventsChannelClient(userEventsChannelRef.current)
  }

  const setDisconnected = () => {
    if (!disconnectedTimeout.current) {
      // Perhaps this should just be a timeout that calls setReconnecting?
      disconnectedTimeout.current = setInterval(() => {
        if (!isReconnecting) {
          console.log('60S DISCONNECTION TIMEOUT CALLING RECONNECTION INTERVAL')
          setReconnecting()
        }
      }, 60_000)
    }
    
    if (!userEventsDisconnectedAt.current) {
      userEventsDisconnectedAt.current = Date.now()
      setUserEventsIsConnected(false)
      setDidSendConnectMessage(false)
    }

    if (!messagesDisconnectedAt.current) {
      messagesDisconnectedAt.current = Date.now()
    }
  }

  // Sets aggressive reconnect timeout 
  const setReconnecting = () => {
    setDisconnected()
    attemptReconnect()
    if (!reconnectTimeout) {
      reconnectTimeout.current = setInterval(() => {
        if (!isReconnecting) {
          console.log('60S RECONNECTION TIMEOUT ATTEMPTING RECONNECT')
          attemptReconnect()
        }
      }, 5_000)
    }
  }

  const attemptReconnect = () => {
    console.log('ATTEMPTING TO REQUEST WEBSOCKET TOKEN')
    consumerRef.current.connection.webSocket.close()
    consumerRef?.current?.disconnect()
    setReAuthToggle(curVal => !curVal)
    setShouldReauthWebsocket(() => true)
  }

  const refreshDataIfStale = (dataRef, dataType, timeAwayThreshold, callback) => {
    if (dataRef.current) {
      const timeAwayInSeconds = Math.floor((Date.now() - dataRef.current) / 1000)
      console.log(`${dataType} away for ${timeAwayInSeconds} seconds`)
      if (timeAwayInSeconds > timeAwayThreshold) {
        console.log(`refreshing ${dataType}`)
        callback(timeAwayInSeconds)
      } else {
        console.log(`not refreshing ${dataType}`)
      }
      dataRef.current = null
    }
  }

  const refreshMessagesIfStale = () => {
    refreshDataIfStale(
      messagesDisconnectedAt,
      'messages',
      10,
      conversationsRefreshRequestRef.current || (() => {})
    )
  }

  const refreshProfilesIfStale = () => {
    // clear the disconnected timeout to re-establish connection from beginning
    clearInterval(disconnectedTimeout.current)
    disconnectedTimeout.current = null
    // clear reconnection timeout as we should be reconnected
    clearInterval(reconnectTimeout.current)
    reconnectTimeout.current = null

    // Refresh user profile data
    refreshDataIfStale(
      userEventsDisconnectedAt,
      'user events (profiles)',
      30,
      () => nearbyProfileRefreshRequest({ removeOldUsersImmediately: false })
    )
  }

  // Broadcasts your presence to surrounding users at new location
  // and updates your broadcasting and their receiving cache accordingly
  const connectSelf = () => {
    if (userEventsChannelRef.current) {
      console.log(`connecting self at ${currentLocationRef.current}`)
      userEventsChannelRef?.current?.perform?.('connected', { lonlat: currentLocationRef.current })
    } else {
      console.log('CONNECTING SELF FAILED BECAUSE USER EVENTS CHANNEL IS NOT INITIALIZED')
    }
  }
  
  // Broadcasts your disappearance to all users in your broadcasting cache
  // and removes you from their maps and grids
  const disappearSelf = () => {
    console.log('disappearing yourself')
    userEventsChannelClient.perform('disappear')
  }

  // Same as disappear, but updates the user to 'hidden' in database
  // preventing them from appearing in new queries
  const hideSelf = (userData) => {
    console.log('hiding yourself')
    handleInstantUpdate({ ...(userData || authState.user), isHidden: true })
    userEventsChannelClient.perform('hide_user')
  }
  
  // Same as appear, but updates the user to 'not hidden' in database
  // allowing them to appear in new queries
  const unhideSelf = (userData) => {
    console.log('unhiding yourself')
    handleInstantUpdate({ ...(userData || authState.user), isHidden: false })
    userEventsChannelClient.perform('unhide_user')
  }
  
  // TODO: The throttle on this isn't working the way we intend because it delays the next pulse by ten seconds rather than preventing it entirely
  const emitActivityPulse = () => {
    if (!activityPulseOnCooldown.current) {
      userEventsChannelClient?.perform('user_activity')
      activityPulseOnCooldown.current = true
      setTimeout(() => {
        activityPulseOnCooldown.current = false
      }, 5_000)
    }
  }

  return (
    <CableContext.Provider value={{
      CableEmitter,
      userEventsChannelClient,
      userEventsChannelRef,
      connectSelf,
      disappearSelf,
      hideSelf,
      unhideSelf,
      emitActivityPulse,
      conversationsRefreshRequestRef,
      shouldReauthWebsocket,
      disconnected: shouldReauthWebsocket,
      isReconnecting,
    }}>
      {children}
    </CableContext.Provider>
  )
}

const useCableContext = () => useContext(CableContext)

export default useCableContext

export { CableContext, CableProvider, useCableContext }
